import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import { BinLocation, WaveItem, WaveResponse } from '../../../services/fulfillment/types';
import { getWave } from '../../../services/fulfillment/waveApi';

// ─── Barcode ─────────────────────────────────────────────────────────────────

interface BarcodeProps {
  value: string;
  height?: number;
  label?: string;
}

const Barcode: React.FC<BarcodeProps> = ({ value, height = 55, label }) => {
  const ref = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!ref.current || !value) return;
    try {
      JsBarcode(ref.current, value, {
        format: 'CODE128',
        width: 2,
        height,
        displayValue: true,
        fontSize: 11,
        margin: 4,
      });
      ref.current.setAttribute('width', '100%');
    } catch {
      // value not encodable in CODE128
    }
  }, [value, height]);

  return (
    <div>
      {label && (
        <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', marginBottom: 2 }}>
          {label}
        </div>
      )}
      <svg ref={ref} style={{ maxWidth: '100%', display: 'block' }} />
    </div>
  );
};

// ─── Item card ────────────────────────────────────────────────────────────────

const ItemCard: React.FC<{ item: WaveItem; index: number }> = ({ item, index }) => {
  const pickBin: BinLocation | undefined = item.binLocations.find(b => b.locationType === 'pick');

  return (
    <div
      style={{
        border: '1px solid #ccc',
        borderRadius: 6,
        padding: '10px 12px',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
        background: '#fff',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600 }}>{item.shopifyOrderName}</span>
        <span style={{ fontSize: 10, color: '#888' }}>
          #{index + 1} · {item.customerName}
        </span>
      </div>
      {item.title && (
        <div style={{ fontSize: 11, color: '#444', marginBottom: 8, lineHeight: 1.3 }}>
          {item.title}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: pickBin ? '1fr 1fr' : '1fr', gap: 8 }}>
        <Barcode value={item.sku} height={50} label="SKU" />
        {pickBin && <Barcode value={pickBin.binCode} height={50} label="Bin" />}
      </div>
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const WaveBarcodePrint: React.FC = () => {
  const { waveId } = useParams<{ waveId: string }>();
  const [wave, setWave] = useState<WaveResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getWave(Number(waveId))
      .then(w => setWave(w))
      .catch(() => setError('Failed to load wave.'))
      .finally(() => setLoading(false));
  }, [waveId]);

  if (loading) {
    return <div style={{ padding: 32, fontFamily: 'sans-serif' }}>Loading wave #{waveId}…</div>;
  }
  if (error || !wave) {
    return (
      <div style={{ padding: 32, fontFamily: 'sans-serif', color: 'red' }}>
        {error ?? 'Wave not found.'}
      </div>
    );
  }

  const activeItems = wave.items.filter(item => item.waveOrderStatus !== 'cancelled');

  const allPickBinCodes = activeItems.flatMap(item =>
    item.binLocations.filter(b => b.locationType === 'pick').map(b => b.binCode),
  );
  const uniqueBinCodes = allPickBinCodes
    .filter((code, i) => allPickBinCodes.indexOf(code) === i)
    .sort();

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
        }
        @page { margin: 0.5in; }
      `}</style>

      <div style={{ fontFamily: 'sans-serif', maxWidth: 960, margin: '0 auto', padding: 16 }}>
        {/* Toolbar */}
        <div
          className="no-print"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: '1px solid #ddd',
          }}
        >
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Wave #{wave.waveId} — Pick Barcodes</div>
            <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
              {activeItems.length} items · {uniqueBinCodes.length} unique bin
              {uniqueBinCodes.length !== 1 ? 's' : ''}
            </div>
          </div>
          <button
            onClick={() => window.print()}
            style={{
              padding: '8px 24px',
              fontSize: 15,
              fontWeight: 600,
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Print
          </button>
        </div>

        {/* Print header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Wave #{wave.waveId} — Pick Sheet</div>
          <div style={{ fontSize: 12, color: '#666' }}>
            {activeItems.length} items · {new Date().toLocaleDateString()}
          </div>
        </div>

        {/* Item grid */}
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          Items to Pick ({activeItems.length})
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 12,
            marginBottom: 32,
          }}
        >
          {activeItems.map((item, i) => (
            <ItemCard key={item.shopifyLineItemGid} item={item} index={i} />
          ))}
        </div>

        {/* Unique bin barcodes */}
        {uniqueBinCodes.length > 0 && (
          <>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 8,
                paddingTop: 16,
                borderTop: '1px solid #ddd',
              }}
            >
              Bin Locations ({uniqueBinCodes.length} unique)
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              {uniqueBinCodes.map(code => (
                <div
                  key={code}
                  style={{
                    border: '1px solid #ccc',
                    borderRadius: 6,
                    padding: '8px 10px',
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    background: '#fff',
                  }}
                >
                  <Barcode value={code} height={55} />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default WaveBarcodePrint;
